import { useEffect, useState, useRef } from "react";
import { titles } from "constants/titles";
import { SYNC_STATUS } from "constants/syncStatus";

/**
 * Custom hook to manage device sync status and button text rotation
 * Matches React Native app implementation
 */
export const useDeviceSyncStatus = (
  deviceDetails,
  defaultButtonText = titles?.refresh_signal
) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [buttonText, setButtonText] = useState(defaultButtonText);
  const [isSyncExpired, setIsSyncExpired] = useState(false);
  const rotationIntervalRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const syncingStartedAt = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rotationIntervalRef?.current) {
        clearInterval(rotationIntervalRef?.current);
      }
      if (syncTimeoutRef?.current) {
        clearTimeout(syncTimeoutRef?.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!deviceDetails) return;

    const { syncHistory } = deviceDetails;

    if (syncHistory?.syncStatus !== SYNC_STATUS.IN_PROGRESS) {
      setButtonText(defaultButtonText);
      clearInterval(rotationIntervalRef?.current);
      clearTimeout(syncTimeoutRef?.current);
      rotationIntervalRef.current = null;
      syncTimeoutRef.current = null;
      syncingStartedAt.current = null;
      setIsSyncExpired(false);
      return;
    }

    const syncingMessages = [
      titles?.refresing_features,
      titles?.refresing_location,
      titles?.refresing_status,
    ];

    if (!syncingStartedAt?.current) {
      syncingStartedAt.current = Date.now();
      setIsSyncExpired(false);

      syncTimeoutRef.current = setTimeout(() => {
        clearInterval(rotationIntervalRef?.current);
        rotationIntervalRef.current = null;
        setIsSyncExpired(true);
        setButtonText(defaultButtonText);
      }, 3 * 60 * 1000 + 10 * 1000); // 3 minutes 10 seconds
    }

    if (isSyncExpired) {
      setButtonText(defaultButtonText);
      clearInterval(rotationIntervalRef?.current);
      rotationIntervalRef.current = null;
      return;
    }

    if (!rotationIntervalRef?.current) {
      rotationIntervalRef.current = setInterval(() => {
        setCurrentMessageIndex(
          (prevIndex) => (prevIndex + 1) % syncingMessages?.length
        );
      }, 2000);
    }

    setButtonText(syncingMessages[currentMessageIndex % syncingMessages?.length]);

    return () => {
      if (rotationIntervalRef?.current) {
        clearInterval(rotationIntervalRef?.current);
        rotationIntervalRef.current = null;
      }
    };
  }, [deviceDetails, currentMessageIndex, isSyncExpired, defaultButtonText]);

  return {
    buttonText,
    isSyncExpired,
    isSyncing:
      deviceDetails?.syncHistory?.syncStatus === SYNC_STATUS.IN_PROGRESS && !isSyncExpired,
  };
};
